import { Strophe } from 'react-strophe'
import Sizzle from 'sizzle'
import chatLog from './chat-log'
import { Chat } from './chatSettingsConfig'
import { getAttributes } from './stanzaHelper'

const checkForPaginatedData = ({ response }) => {
  const hasMoreData = Sizzle(`query set[xmlns="${Chat.NS.RSM}"]`, response)
  if (hasMoreData?.length) {
    console.error(`** There may be more data! **`, response)
  }
}

/*
  Parse a message from the server that has been archived; chat history.
*/
export const parseArchivedMessageReceived = ({ stanza, isGroup }) => {
  let data
  const result = Sizzle(`message > result`, stanza).pop()
  if (result) {
    const messageEl = Sizzle('message[from]', result).pop()

    let fromJid
    if (isGroup) {
      const jid = Strophe.getResourceFromJid(messageEl?.getAttribute('from'))
      fromJid = `${jid}@${Chat.Settings.DOMAIN}`
    } else {
      fromJid = Strophe.getBareJidFromJid(messageEl?.getAttribute('from'))
    }

    const rtcMessage = Sizzle(`message > rtc-message`, stanza)?.pop()
    const displayName = rtcMessage?.getAttribute('from') || null

    const message = Sizzle(`message > body`, result)?.pop()?.textContent

    data = {
      id: result.getAttribute('id'),
      message,
      displayName,
      fromJid,
      timestamp: Sizzle('delay', result)
        ?.pop()
        ?.getAttribute('stamp'),
    }
  }

  return data
}

/*
  Converts the presence notification to a simple "isOnline" boolean.
*/
export const isPersonOnline = ({ presence }) => {
  let isOnline

  if (presence?.type === 'unavailable') {
    isOnline = false
  } else if (!presence.type && presence?.show === '') {
    isOnline = true
  } else {
    // show is xa, gone, etc which we don't allow them to change right now.
    isOnline = false
  }

  return { jid: presence.jid, isOnline }
}

/*
  Parse a presence (status) notification for another person, online etc.
*/
export const parsePresenceNotification = ({ stanza }) => {
  if (!stanza.getElementsByTagName('presence')) {
    chatLog('[Presence] Expected a <presence /> stanza, but received: ', Strophe.serialize(stanza))
    return null
  }

  const { type, to, from } = getAttributes(stanza)

  const toJid = Strophe.getBareJidFromJid(to)
  const jid = Strophe.getBareJidFromJid(from)
  const show = Sizzle('show', stanza)?.pop()?.textContent || ''
  const status = Sizzle('status', stanza)?.pop()?.textContent || ''
  const isSelf = jid === toJid

  if (type === 'subscribe') {
    chatLog('[Presence] Found someone trying to add me', Strophe.serialize(stanza))
  } else if (type === 'unavailable') {
    // chatLog(`[Presence] ${jid} has logged out.`)
  } else if (jid.indexOf(Chat.Settings.MUC_DOMAIN) > -1) {
    // chatLog(`[Presence] ${jid} is indicating their presence.`)
  } else if (!type) {
    // chatLog(`[Presence] ${jid} is now online`)
  } else if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('TODO: should we handle this type?', stanza)
  }

  return { isSelf, jid, show, status, type }
}

/*
  Parse a message notification for a direct chat OR group chat.
  NOTE: There is more information that can be parsed here. For
  individual chats, we just pull the history back again rather
  than just adding the received message to the UI. This is because
  when sending a message, we receive no response if it was successfully
  sent. By pulling the history again, we can verify the message was 
  actually sent. So it's simpler to always pull the history rather
  than process this incoming message and add it to the UI directly.
*/
export const parseMessageChatNotification = ({ stanza }) => {
  const type = stanza.getAttribute('type')
  const elems = stanza.getElementsByTagName('body')

  // If groupchat, parse chat room, ignoring who actually sent it.
  let fromJid = Strophe.getBareJidFromJid(stanza.getAttribute('from'))
  let toJid = Strophe.getBareJidFromJid(stanza.getAttribute('to'))

  const isCarbonCopy = Sizzle(`sent[xmlns="${Chat.NS.CARBONS}"]`, stanza)?.pop()
  const actualMessage = Sizzle(`message message`, stanza)?.pop()
  if (isCarbonCopy && actualMessage) {
    chatLog('[Message] Found a carbon copy message. Getting the real to and from.')
    toJid = Strophe.getBareJidFromJid(actualMessage.getAttribute('to'))
    fromJid = Strophe.getBareJidFromJid(actualMessage.getAttribute('from'))
  }

  const isChat = type === 'chat' || type === 'groupchat'
  const hasMessage = elems.length > 0

  let message
  if (isChat && hasMessage) {
    const body = elems[0]
    message = Strophe.getText(body)
  }

  return { type, fromJid, toJid, message }
}

/*
  Parse a chat status notification from a chat.
  This includes what the other people are doing, such as:
  typing, active, inactive, paused typing, etc.
  https://xmpp.org/extensions/xep-0085.html#table-1
*/
export const parseNonMessageChatNotification = ({ stanza }) => {
  const node = Sizzle(
    `composing[xmlns="${Chat.NS.CHATSTATES}"], paused[xmlns="${Chat.NS.CHATSTATES}"], inactive[xmlns="${Chat.NS.CHATSTATES}"], active[xmlns="${Chat.NS.CHATSTATES}"], gone[xmlns="${Chat.NS.CHATSTATES}"]`,
    stanza
  ).pop()?.nodeName

  return node === Chat.ChatState.COMPOSING
}

/*
  Parse the people from the roster response.
*/
export const parsePeopleFromRoster = (response) => {
  const toJid = Strophe.getBareJidFromJid(response.getAttribute('to'))
  const itemsEl = Sizzle(`query > item`, response)
  let list = []

  if (itemsEl && Array.from(itemsEl)?.length > 0) {
    list = Array.from(itemsEl)
      .map((stanza) => {
        return {
          jid: stanza.getAttribute('jid'),
          ask: stanza.getAttribute('ask'),
          subscription: stanza.getAttribute('subscription'),
        }
      })
      .filter((person) => person.jid !== toJid)

    checkForPaginatedData({ response })
  }
  return list
}

/*
  Parse the group information.
  NOTE: Not all fields are currently being processed.
*/
export const parseGroupFromSearchResults = (response) => {
  const jid = response.getAttribute('from')
  const friendlyName = Sizzle('query x field[var="muc#roomconfig_roomname"] value', response)?.pop()?.textContent

  return [
    {
      jid,
      friendlyName,
    },
  ]
}

export const parseGroupChatMembers = (response) => {
  const itemsEl = Sizzle(`query > item`, response)
  const list = Array.from(itemsEl).map((item) => {
    return {
      jid: item.getAttribute('jid'),
      name: item.getAttribute('name'),
    }
  })
  return list
}

export const parseGroupChatMembersCount = (response) => {
  const list = parseGroupChatMembers(response)
  return list?.length || 0
}

export const parseForItemNotFound = (errorResponse) => {
  const itemNotFoundEl = errorResponse?.stanza?.getElementsByTagName('item-not-Found')
  return itemNotFoundEl
}

export const parseForImpromptu = (stanza) => {
  const impromptuEl = stanza?.getElementsByTagName('impromptu')
  return impromptuEl
}

/*
  Parse the details from the RSM (result set management) node.
  If 'complete=true', then we are done paging.
*/
export const parsePaginationDetails = (stanza) => {
  const first = Sizzle('fin set first', stanza)?.pop()?.textContent
  const last = Sizzle('fin set last', stanza)?.pop()?.textContent
  const isComplete =
    Sizzle('fin', stanza)
      ?.pop()
      ?.getAttribute('complete') || false

  return { isComplete: isComplete === 'true', first, last }
}
